import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  detectLayerIssues,
  effectiveThemeForSlide,
  getEffectiveCalmZones,
  isComposedBackgroundMode,
  sortedLayers,
  surfPreviewBackgroundStyle,
  surfPreviewScrimStyle,
  type GuideLine,
  type LayerIssue,
  type SurfDeck,
  type SurfSlide,
} from "@vida/surf-core";
import { EditorLayer } from "./EditorLayer";

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;
const CANVAS_MARGIN_PX = 56;

type Props = {
  deck: SurfDeck;
  slide: SurfSlide;
  slideBackgroundUrl?: string | null;
  layerImageUrls?: Record<string, string>;
  selectedLayerIds: string[];
  showGuides: boolean;
  readOnly?: boolean;
  compact?: boolean;
  onSelectLayer: (id: string | null, additive?: boolean) => void;
  onLayerRect: (layerId: string, rect: { x: number; y: number; w: number; h: number }) => void;
  onLayerContent: (layerId: string, content: string) => void;
  onLayerGraphic?: (layerId: string, graphic: import("@vida/surf-core").SurfGraphicData) => void;
};

export function SlideCanvas({
  deck,
  slide,
  slideBackgroundUrl,
  layerImageUrls = {},
  selectedLayerIds,
  showGuides,
  readOnly = false,
  compact = false,
  onSelectLayer,
  onLayerRect,
  onLayerContent,
  onLayerGraphic,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeGuides, setActiveGuides] = useState<GuideLine[]>([]);
  const [zoom, setZoom] = useState(1);
  const interactive = !readOnly && !compact;

  const slideTheme = useMemo(
    () => effectiveThemeForSlide(deck.theme, slide),
    [deck.theme, slide],
  );
  const safe = getEffectiveCalmZones(slide, deck.theme)[0];
  // No full-slide scrim in composed-background mode: the bg already reserves calm
  // zones and raster/text widgets carry their own panel → a global scrim only veils.
  const scrim = isComposedBackgroundMode(slideTheme) ? null : surfPreviewScrimStyle(slideTheme);
  const layers = sortedLayers(slide);
  const issues = useMemo(() => detectLayerIssues(slide, slideTheme), [slide, slideTheme]);
  const issuesByLayer = useMemo(() => {
    const map = new Map<string, LayerIssue[]>();
    for (const issue of issues) {
      const list = map.get(issue.layerId) ?? [];
      list.push(issue);
      map.set(issue.layerId, list);
    }
    return map;
  }, [issues]);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  }, []);

  const onWheel = useCallback((e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) =>
      Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 100) / 100)),
    );
  }, []);

  useEffect(() => {
    if (!interactive) return;
    const el = viewportRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [interactive, onWheel]);

  const slideBody = (
    <div
      ref={containerRef}
      className={`slide-canvas${readOnly ? " slide-canvas--readonly" : ""}${
        interactive ? " slide-canvas--interactive" : ""
      }`}
      style={surfPreviewBackgroundStyle(deck.theme, slideBackgroundUrl ?? null, slide.background.solidColor)}
      onPointerDown={readOnly ? undefined : () => onSelectLayer(null)}
    >
      {scrim && <div className="slide-canvas-scrim" style={scrim} aria-hidden />}
      {showGuides && safe && (
        <div
          className="slide-canvas-safezone"
          style={{
            left: `${safe.x * 100}%`,
            top: `${safe.y * 100}%`,
            width: `${safe.w * 100}%`,
            height: `${safe.h * 100}%`,
          }}
          aria-hidden
        />
      )}
      {showGuides &&
        activeGuides.map((g, i) => (
          <div
            key={`${g.axis}-${g.value}-${g.kind}-${i}`}
            className="slide-canvas-guide"
            style={
              g.axis === "x"
                ? { left: `${g.value * 100}%`, top: 0, width: 1, height: "100%" }
                : { top: `${g.value * 100}%`, left: 0, height: 1, width: "100%" }
            }
            aria-hidden
          />
        ))}
      {layers.map((layer) => (
        <EditorLayer
          key={layer.id}
          layer={layer}
          theme={slideTheme}
          selected={!readOnly && selectedLayerIds.includes(layer.id)}
          safeZone={safe}
          otherLayers={layers.filter((l) => l.id !== layer.id)}
          imageUrl={layerImageUrls[layer.id] ?? null}
          containerRef={containerRef}
          onGuidesChange={readOnly ? () => undefined : setActiveGuides}
          onSelect={
            readOnly
              ? () => undefined
              : (additive) => {
                  if (additive) onSelectLayer(layer.id, true);
                  else onSelectLayer(layer.id);
                }
          }
          onRectChange={readOnly ? () => undefined : (rect) => onLayerRect(layer.id, rect)}
          onContentChange={
            readOnly ? () => undefined : (content) => onLayerContent(layer.id, content)
          }
          onGraphicChange={
            readOnly || !onLayerGraphic
              ? undefined
              : (graphic) => onLayerGraphic(layer.id, graphic)
          }
        />
      ))}
    </div>
  );

  return (
    <div className={`slide-canvas-wrap${compact ? " slide-canvas-wrap--compact" : ""}`}>
      {!readOnly && issues.length > 0 && (
        <div className="slide-canvas-warnings" role="status">
          {issues.map((issue, i) => (
            <span key={`${issue.layerId}-${issue.kind}-${i}`} className="layer-warning-badge">
              {issue.message}
            </span>
          ))}
        </div>
      )}
      {interactive && (
        <div className="slide-canvas-zoom-bar" role="group" aria-label="Zoom slide">
          <button type="button" className="surf-btn surf-btn--ghost" onClick={zoomOut} disabled={zoom <= ZOOM_MIN}>
            −
          </button>
          <span className="slide-canvas-zoom-label">{Math.round(zoom * 100)}%</span>
          <button type="button" className="surf-btn surf-btn--ghost" onClick={zoomIn} disabled={zoom >= ZOOM_MAX}>
            +
          </button>
          <button type="button" className="surf-btn surf-btn--ghost" onClick={() => setZoom(1)}>
            100%
          </button>
          <span className="muted slide-canvas-zoom-hint">⌘/Ctrl + rotella</span>
        </div>
      )}
      {interactive ? (
        <div
          ref={viewportRef}
          className="slide-canvas-viewport slide-canvas-viewport--interactive"
        >
          <div
            className="slide-canvas-stage"
            style={{ transform: `scale(${zoom})`, padding: CANVAS_MARGIN_PX }}
          >
            {slideBody}
          </div>
        </div>
      ) : (
        slideBody
      )}
      {selectedLayerIds.some((id) => issuesByLayer.has(id)) && (
        <p className="muted layer-warning-hint">Suggerimento: usa «Riorganizza» o sposta il layer nella safe zone.</p>
      )}
    </div>
  );
}
